import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CATALOG from '../data/catalog';
import { useAuth } from '../contexts/AuthContext';
import { imageDatabase } from '../data/imageDatabase';
import {
  ChevronLeft, Check, Save, ShoppingBag, Minus, Plus, Truck, Shield,
  Star, Heart, Share2, Pencil, Flame, RefreshCcw
} from 'lucide-react';

// ─── OPTIONS ───
const WORK_OPTIONS = [
  { id: 'none', name: 'None', price: 0, desc: 'Plain finish' },
  { id: 'light', name: 'Light', price: 300, desc: 'Minimal thread work' },
  { id: 'medium', name: 'Medium', price: 600, desc: 'Detailed embroidery' },
  { id: 'heavy', name: 'Heavy', price: 1200, desc: 'Full handwork' },
];

const FABRIC_OPTIONS = [
  { id: 'cotton', name: 'Cotton', price: 0, swatch: '#F5F0E1' },
  { id: 'silk', name: 'Silk', price: 500, swatch: '#E8D5B7' },
  { id: 'velvet', name: 'Velvet', price: 800, swatch: '#722F37' },
  { id: 'georgette', name: 'Georgette', price: 400, swatch: '#D4C5A9' },
  { id: 'linen', name: 'Linen', price: 200, swatch: '#C9B99A' },
  { id: 'chiffon', name: 'Chiffon', price: 350, swatch: '#F5E8E4' },
];

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom'];

const COLOR_OPTIONS = [
  { id: 'default', name: 'Default', hex: null, price: 0 },
  { id: 'ivory', name: 'Ivory', hex: '#FFFFF0', price: 0 },
  { id: 'blush', name: 'Blush', hex: '#E5B2B9', price: 200 },
  { id: 'navy', name: 'Navy', hex: '#1B2A4A', price: 200 },
  { id: 'emerald', name: 'Emerald', hex: '#2E5945', price: 200 },
  { id: 'maroon', name: 'Maroon', hex: '#6B1D2A', price: 200 },
  { id: 'gold', name: 'Gold', hex: '#D4AF37', price: 300 },
  { id: 'black', name: 'Black', hex: '#1A1A1A', price: 100 },
];

// Text+price pill options for men / fallback
const SLEEVE_PILLS = [
  { id: 'short', name: 'Short', price: 0 },
  { id: 'three-quarter', name: '3/4th', price: 100 },
  { id: 'full', name: 'Full', price: 150 },
  { id: 'bell', name: 'Bell', price: 250 },
  { id: 'sleeveless', name: 'Sleeveless', price: 0 },
];
const FRONT_PILLS = [
  { id: 'plain', name: 'Plain', price: 0 },
  { id: 'embroidered', name: 'Embroidered', price: 400 },
  { id: 'printed', name: 'Printed', price: 300 },
  { id: 'pleated', name: 'Pleated', price: 200 },
];
const BACK_PILLS = [
  { id: 'plain', name: 'Plain', price: 0 },
  { id: 'deep-v', name: 'Deep V', price: 200 },
  { id: 'keyhole', name: 'Keyhole', price: 250 },
  { id: 'lace', name: 'Lace Panel', price: 350 },
];
const NECK_PILLS = [
  { id: 'round', name: 'Round', price: 0 },
  { id: 'v-neck', name: 'V-Neck', price: 100 },
  { id: 'boat', name: 'Boat', price: 150 },
  { id: 'collar', name: 'Collar', price: 200 },
  { id: 'mandarin', name: 'Mandarin', price: 180 },
  { id: 'sweetheart', name: 'Sweetheart', price: 250 },
];

// Attach a price to each image-design option (repeating pattern for predictability)
const DESIGN_PRICES = [0, 150, 200, 250, 300, 350, 400, 150, 200, 250];
const addPrices = (arr = []) => arr.map((o, i) => ({ ...o, price: DESIGN_PRICES[i % DESIGN_PRICES.length] }));

const DESIGN_DEFAULTS = { work: 'none', fabric: 'cotton', sleeves: null, front: null, back: null, neck: null, color: 'default', size: 'M' };

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const DesignStudio = () => {
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();
  const isPrivileged = ['founder', 'admin'].includes(user?.role);
  const location = useLocation();
  const { gender = 'female', category = 'blouse', categoryName = 'Custom Design', basePrice = '₹999', productId, productName, productImage } = location.state || {};

  const parsedBase = parseInt(String(basePrice).replace(/[^\d]/g, ''), 10) || 999;
  const isWomen = gender === 'female';

  // Pull image-based design options from imageDatabase (gender-aware)
  const dbKey = `${gender === 'male' ? 'men' : 'women'}-${category}`;
  const db = imageDatabase[dbKey];
  const imgFront = useMemo(() => addPrices((db?.frontDesigns || db?.neckDesigns || []).slice(0, 10)), [db]);
  const imgBack = useMemo(() => addPrices((db?.backDesigns || []).slice(0, 10)), [db]);
  const imgSleeve = useMemo(() => addPrices((db?.sleeveDesigns || []).slice(0, 10)), [db]);
  const imgNeck = useMemo(() => addPrices((db?.neckDesigns || db?.frontDesigns || []).slice(0, 8)), [db]);

  const [saved, setSaved] = useState(false);
  const [liked, setLiked] = useState(false);
  const [qty, setQty] = useState(1);
  const [sel, setSel] = useState(DESIGN_DEFAULTS);
  const [editing, setEditing] = useState('neck');
  // Work/Fabric pickers are shown only when backend says Trial Mode is OFF (fail-safe: hidden).
  const [trialMode, setTrialMode] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/config`)
      .then(r => r.json()).then(c => setTrialMode(c.trial_mode !== false)).catch(() => setTrialMode(true));
  }, []);

  const pick = (key, val) => {
    setSel(p => ({ ...p, [key]: val }));
    // Auto-collapse (close picker) on selection — user can reopen via Change
    setEditing(null);
  };

  // helper: readable label for current selection
  const labelFor = (key) => {
    const v = sel[key];
    if (!v) return '—';
    switch (key) {
      case 'work': return WORK_OPTIONS.find(o => o.id === v)?.name;
      case 'fabric': return FABRIC_OPTIONS.find(o => o.id === v)?.name;
      case 'sleeves': return isWomen ? imgSleeve.find(o => o.id === v)?.name : SLEEVE_PILLS.find(o => o.id === v)?.name;
      case 'front': return isWomen ? imgFront.find(o => o.id === v)?.name : FRONT_PILLS.find(o => o.id === v)?.name;
      case 'back': return isWomen ? imgBack.find(o => o.id === v)?.name : BACK_PILLS.find(o => o.id === v)?.name;
      case 'neck': return isWomen ? imgNeck.find(o => o.id === v)?.name : NECK_PILLS.find(o => o.id === v)?.name;
      case 'color': return COLOR_OPTIONS.find(o => o.id === v)?.name;
      case 'size': return v;
      default: return v;
    }
  };

  const thumbFor = (key) => {
    const v = sel[key];
    if (!v) return null;
    switch (key) {
      case 'sleeves': return isWomen ? imgSleeve.find(o => o.id === v)?.image : null;
      case 'front': return isWomen ? imgFront.find(o => o.id === v)?.image : null;
      case 'back': return isWomen ? imgBack.find(o => o.id === v)?.image : null;
      case 'neck': return isWomen ? imgNeck.find(o => o.id === v)?.image : null;
      default: return null;
    }
  };

  const priceFor = (key) => {
    const v = sel[key];
    if (!v) return 0;
    switch (key) {
      case 'work': return WORK_OPTIONS.find(o => o.id === v)?.price || 0;
      case 'fabric': return FABRIC_OPTIONS.find(o => o.id === v)?.price || 0;
      case 'sleeves': return isWomen ? (imgSleeve.find(o => o.id === v)?.price || 0) : (SLEEVE_PILLS.find(o => o.id === v)?.price || 0);
      case 'front': return isWomen ? (imgFront.find(o => o.id === v)?.price || 0) : (FRONT_PILLS.find(o => o.id === v)?.price || 0);
      case 'back': return isWomen ? (imgBack.find(o => o.id === v)?.price || 0) : (BACK_PILLS.find(o => o.id === v)?.price || 0);
      case 'neck': return isWomen ? (imgNeck.find(o => o.id === v)?.price || 0) : (NECK_PILLS.find(o => o.id === v)?.price || 0);
      case 'color': return COLOR_OPTIONS.find(o => o.id === v)?.price || 0;
      default: return 0;
    }
  };

  const customPrice = useMemo(() => {
    return ['work','fabric','sleeves','front','back','neck','color'].reduce((sum, k) => sum + priceFor(k), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, isWomen, imgFront, imgBack, imgSleeve, imgNeck]);

  const unitPrice = parsedBase + customPrice;
  const totalPrice = unitPrice * qty;

  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 10);
  const deliveryStr = deliveryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  // Related products
  const gk = isWomen ? 'female' : 'male';
  const catProducts = CATALOG[gk]?.[category]?.products || [];
  const related = catProducts.filter(p => p.id !== productId).slice(0, 6);

  // ─── Resolve label + image URL per selection for persistence ───
  const buildSelectionDetails = () => {
    const details = {};
    const addEntry = (key, label, image) => {
      if (!label && !image) return;
      details[key] = { label: label || '', image: image || '' };
    };
    // Text-only selections
    const workOpt = WORK_OPTIONS.find(o => o.id === sel.work);
    if (workOpt) addEntry('work', workOpt.name, null);
    const fabOpt = FABRIC_OPTIONS.find(o => o.id === sel.fabric);
    if (fabOpt) addEntry('fabric', fabOpt.name, null);
    const colOpt = COLOR_OPTIONS.find(o => o.id === sel.color);
    if (colOpt) addEntry('color', colOpt.name, null);
    if (sel.size) addEntry('size', sel.size, null);
    // Image-based (women) or pill-based (men)
    const resolveImage = (id, imgPool, textPool) => {
      if (!id) return null;
      const imgMatch = isWomen ? imgPool.find(o => o.id === id) : null;
      if (imgMatch) return { label: imgMatch.name, image: imgMatch.image };
      const textMatch = textPool.find(o => o.id === id);
      return textMatch ? { label: textMatch.name, image: '' } : null;
    };
    const front = resolveImage(sel.front, imgFront, FRONT_PILLS);
    if (front) addEntry('front', front.label, front.image);
    const back = resolveImage(sel.back, imgBack, BACK_PILLS);
    if (back) addEntry('back', back.label, back.image);
    const sleeves = resolveImage(sel.sleeves, imgSleeve, SLEEVE_PILLS);
    if (sleeves) addEntry('sleeves', sleeves.label, sleeves.image);
    const neck = resolveImage(sel.neck, imgNeck, NECK_PILLS);
    if (neck) addEntry('neck', neck.label, neck.image);
    return details;
  };

  const buildOrderState = () => {
    const selectionDetails = buildSelectionDetails();
    // Build simplified label-only object (backward compat for existing backend expecting strings)
    const labels = Object.fromEntries(Object.entries(selectionDetails).map(([k, v]) => [k, v.label]));
    const orderState = {
        gender, category,
        categoryName: productName || categoryName,
        basePrice: `₹${parsedBase * qty}`,
        selections: sel,
        selectionDetails, // { front: {label, image}, ... } — consumed by Checkout → orders.customizations
        selectionLabels: labels,
        customizationPrice: customPrice * qty,
        quantity: qty,
        productImage
    };
    return orderState;
  };

  // Admin-only: skip body scan and go straight to checkout. Backend re-checks the role.
  const handleAdminTestCheckout = async () => {
    const session = await checkAuth();
    if (!['founder', 'admin'].includes(session?.role)) return;
    navigate('/checkout', { state: { ...buildOrderState(), adminTest: true } });
  };

  const handleOrder = async () => {
    const orderState = buildOrderState();
    // Verify the real session (not cached user state) before Body Measurement.
    if (!(await checkAuth())) {
      navigate('/auth', { state: { redirectTo: '/measurements', redirectState: orderState } });
      return;
    }
    navigate('/measurements', { state: orderState });
  };

  // ─── Selected Summary Card (collapsed state) ───
  const SelectedCard = ({ id, title, subtitle }) => {
    const thumb = thumbFor(id);
    const price = priceFor(id);
    const value = labelFor(id);
    return (
      <button data-testid={`selected-${id}`} onClick={() => setEditing(id)}
        className="w-full flex items-center gap-3 px-4 py-3 transition-all hover:bg-[#FAF9F6]"
        style={{ borderTop: `1px solid ${LINE}` }}>
        {thumb ? (
          <div className="w-11 h-14 flex-shrink-0 bg-[#FAF9F6] overflow-hidden">
            <img src={thumb} alt={value} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ) : (
          <div className="w-11 h-14 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: SOFT, border: `1px solid ${LINE}` }}>
            {id === 'fabric' && <span className="w-6 h-6 rounded-full" style={{ backgroundColor: FABRIC_OPTIONS.find(o => o.id === sel.fabric)?.swatch }} />}
            {id === 'color' && <span className="w-6 h-6 rounded-full border" style={{ backgroundColor: COLOR_OPTIONS.find(o => o.id === sel.color)?.hex || SOFT, borderColor: LINE }} />}
            {id === 'size' && <span className="text-[12px] font-medium" style={{ color: INK }}>{sel.size}</span>}
            {id === 'work' && <Check className="w-4 h-4" style={{ color: GOLD }} strokeWidth={1.5} />}
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <p className="text-[9px] tracking-[0.12em] uppercase mb-0.5" style={{ color: MUTED }}>{title}</p>
          <p className="text-[13px] truncate" style={{ color: INK, fontWeight: 500 }}>{value}</p>
          {subtitle && <p className="text-[10px]" style={{ color: MUTED }}>{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          {price > 0 && <span className="text-[10px]" style={{ color: GOLD }}>+₹{price}</span>}
          <span className="flex items-center gap-1 text-[10px] px-2 py-1" style={{ color: INK, border: `1px solid ${LINE}`, borderRadius: 999 }}>
            <Pencil className="w-3 h-3" strokeWidth={1.5} /> Change
          </span>
        </div>
      </button>
    );
  };

  // ─── Expanded Picker Header ───
  const PickerHeader = ({ id, title, count }) => (
    <div className="flex items-center justify-between px-4 pt-4 pb-2">
      <div>
        <p className="text-[9px] tracking-[0.14em] uppercase" style={{ color: GOLD }}>Select</p>
        <h3 className="text-[15px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>{title}</h3>
      </div>
      {sel[id] && (
        <button data-testid={`close-${id}`} onClick={() => setEditing(null)} className="text-[11px] flex items-center gap-1" style={{ color: MUTED }}>
          <RefreshCcw className="w-3 h-3" strokeWidth={1.5} /> Done
        </button>
      )}
      {count != null && !sel[id] && <span className="text-[10px]" style={{ color: MUTED }}>{count} options</span>}
    </div>
  );

  // ─── Image Grid Picker (women) ───
  const ImagePicker = ({ id, options, testPrefix }) => (
    <div className="px-4 pb-4 grid grid-cols-3 gap-2">
      {options.map(opt => {
        const active = sel[id] === opt.id;
        return (
          <button key={opt.id} data-testid={`${testPrefix}-${opt.id}`} onClick={() => pick(id, opt.id)}
            className="relative bg-white overflow-hidden transition-all"
            style={{ border: `${active ? 2 : 1}px solid ${active ? GOLD : LINE}` }}>
            <div className="aspect-[3/4] overflow-hidden bg-[#FAF9F6]">
              <img src={opt.image} alt={opt.name} loading="lazy" decoding="async"
                className="w-full h-full object-cover"
                onError={e => { e.target.style.opacity = '0.3'; }} />
              {active && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(212,175,55,0.25)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: GOLD }}>
                    <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                </div>
              )}
            </div>
            <div className="p-1.5">
              <p className="text-[10px] leading-tight line-clamp-1" style={{ color: INK }}>{opt.name}</p>
              {opt.price > 0 && <p className="text-[9px]" style={{ color: GOLD }}>+₹{opt.price}</p>}
            </div>
          </button>
        );
      })}
    </div>
  );

  // ─── Pill Picker (men / fallback) ───
  const PillPicker = ({ id, options, testPrefix }) => (
    <div className="px-4 pb-4 flex flex-wrap gap-1.5">
      {options.map(opt => {
        const active = sel[id] === opt.id;
        return (
          <button key={opt.id} data-testid={`${testPrefix}-${opt.id}`} onClick={() => pick(id, opt.id)}
            className="px-3.5 py-2.5 text-[12px] transition-all flex items-center gap-1.5"
            style={{ backgroundColor: active ? INK : '#FFF', color: active ? '#FFF' : INK, border: `1px solid ${active ? INK : LINE}` }}>
            {opt.name}
            {opt.price > 0 && <span className="text-[9px]" style={{ color: active ? 'rgba(255,255,255,0.7)' : GOLD }}>+₹{opt.price}</span>}
          </button>
        );
      })}
    </div>
  );

  // Render either selected-card OR expanded picker for each section
  const renderSection = (id, title) => {
    const isOpen = editing === id;
    const hasSel = !!sel[id];

    if (!isOpen && hasSel) return <SelectedCard key={id} id={id} title={title} />;

    // Open picker
    return (
      <div key={id} className="bg-white" style={{ borderTop: `1px solid ${LINE}` }}>
        <PickerHeader id={id} title={title} />
        {id === 'work' && (
          <div className="px-4 pb-4 space-y-1.5">
            {WORK_OPTIONS.map(opt => {
              const active = sel.work === opt.id;
              return (
                <button key={opt.id} data-testid={`work-${opt.id}`} onClick={() => pick('work', opt.id)}
                  className="w-full flex items-center justify-between p-3 transition-all"
                  style={{ backgroundColor: active ? 'rgba(212,175,55,0.06)' : '#FFF', border: `1px solid ${active ? GOLD : LINE}` }}>
                  <div className="text-left">
                    <p className="text-[13px]" style={{ color: INK, fontWeight: active ? 500 : 400 }}>{opt.name}</p>
                    <p className="text-[10px]" style={{ color: MUTED }}>{opt.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px]" style={{ color: opt.price > 0 ? GOLD : MUTED }}>{opt.price > 0 ? `+₹${opt.price}` : 'Free'}</span>
                    {active && <Check className="w-4 h-4" style={{ color: GOLD }} strokeWidth={2} />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {id === 'fabric' && (
          <div className="px-4 pb-4 grid grid-cols-3 gap-2">
            {FABRIC_OPTIONS.map(opt => {
              const active = sel.fabric === opt.id;
              return (
                <button key={opt.id} data-testid={`fabric-${opt.id}`} onClick={() => pick('fabric', opt.id)}
                  className="flex flex-col items-center gap-1 p-2.5 transition-all bg-white"
                  style={{ border: `${active ? 2 : 1}px solid ${active ? GOLD : LINE}` }}>
                  <div className="w-11 h-11 rounded-full border" style={{ backgroundColor: opt.swatch, borderColor: LINE }}>
                    {active && <Check className="w-full h-full p-2" style={{ color: opt.swatch === '#722F37' ? '#FFF' : GOLD }} strokeWidth={2.5} />}
                  </div>
                  <p className="text-[11px]" style={{ color: INK }}>{opt.name}</p>
                  <p className="text-[9px]" style={{ color: opt.price > 0 ? GOLD : MUTED }}>{opt.price > 0 ? `+₹${opt.price}` : 'Incl.'}</p>
                </button>
              );
            })}
          </div>
        )}
        {id === 'sleeves' && (isWomen && imgSleeve.length > 0
          ? <ImagePicker id="sleeves" options={imgSleeve} testPrefix="sleeves" />
          : <PillPicker id="sleeves" options={SLEEVE_PILLS} testPrefix="sleeves" />)}
        {id === 'front' && (isWomen && imgFront.length > 0
          ? <ImagePicker id="front" options={imgFront} testPrefix="front" />
          : <PillPicker id="front" options={FRONT_PILLS} testPrefix="front" />)}
        {id === 'back' && (isWomen && imgBack.length > 0
          ? <ImagePicker id="back" options={imgBack} testPrefix="back" />
          : <PillPicker id="back" options={BACK_PILLS} testPrefix="back" />)}
        {id === 'neck' && (isWomen && imgNeck.length > 0
          ? <ImagePicker id="neck" options={imgNeck} testPrefix="neck" />
          : <PillPicker id="neck" options={NECK_PILLS} testPrefix="neck" />)}
        {id === 'color' && (
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {COLOR_OPTIONS.map(opt => {
              const active = sel.color === opt.id;
              return (
                <button key={opt.id} data-testid={`color-${opt.id}`} onClick={() => pick('color', opt.id)}
                  className="flex flex-col items-center gap-1 p-2 transition-all"
                  style={{ border: `${active ? 2 : 1}px solid ${active ? GOLD : LINE}`, minWidth: 58 }}>
                  <div className="w-8 h-8 rounded-full border" style={{ backgroundColor: opt.hex || SOFT, borderColor: LINE }}>
                    {active && <Check className="w-full h-full p-1.5" style={{ color: opt.hex && opt.hex !== '#FFFFF0' ? '#FFF' : GOLD }} strokeWidth={2.5} />}
                  </div>
                  <span className="text-[10px]" style={{ color: MUTED }}>{opt.name}</span>
                  {opt.price > 0 && <span className="text-[9px]" style={{ color: GOLD }}>+₹{opt.price}</span>}
                </button>
              );
            })}
          </div>
        )}
        {id === 'size' && (
          <div className="px-4 pb-4">
            <div className="flex flex-wrap gap-1.5">
              {SIZE_OPTIONS.map(s => {
                const active = sel.size === s;
                return (
                  <button key={s} data-testid={`size-${s}`} onClick={() => pick('size', s)}
                    className="w-12 h-11 flex items-center justify-center text-[12px] transition-all"
                    style={{ backgroundColor: active ? INK : '#FFF', color: active ? '#FFF' : INK, border: `1px solid ${active ? INK : LINE}` }}>
                    {s}
                  </button>
                );
              })}
            </div>
            {sel.size === 'Custom' && <p className="text-[10px] mt-2" style={{ color: GOLD }}>AI sizing will measure you in the next step</p>}
          </div>
        )}
      </div>
    );
  };

  const SECTIONS = [
    ...(trialMode ? [] : [
      { id: 'work', title: 'Work Level' },
      { id: 'fabric', title: 'Fabric' },
    ]),
    ...(isWomen ? [
      { id: 'neck', title: 'Neck Style' },
      { id: 'front', title: 'Front Design' },
      { id: 'back', title: 'Back Design' },
      { id: 'sleeves', title: 'Sleeve Style' },
    ] : [
      { id: 'neck', title: 'Collar / Neck' },
      { id: 'front', title: 'Front Style' },
      { id: 'back', title: 'Back Style' },
      { id: 'sleeves', title: 'Sleeve Style' },
    ]),
    // { id: 'color', title: 'Color' },          // temporarily hidden — used again in future
    { id: 'size', title: 'Size' },
  ];

  const selectedCount = SECTIONS.filter(s => sel[s.id]).length;
  const progressPct = Math.round((selectedCount / SECTIONS.length) * 100);

  return (
    <div data-testid="design-studio" className="min-h-screen pb-28" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-3 py-3 border-b"
        style={{ backgroundColor: 'rgba(250,249,246,0.95)', backdropFilter: 'blur(12px)', borderColor: LINE }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-1 -ml-1">
          <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
        </button>
        <p className="text-[10px] tracking-[0.15em] uppercase" style={{ color: GOLD }}>Customize</p>
        <div className="flex items-center gap-1.5">
          <button data-testid="share-btn" className="p-1.5"><Share2 className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} /></button>
          <button data-testid="save-btn" onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); }} className="p-1.5">
            {saved ? <Check className="w-4 h-4" style={{ color: '#166534' }} strokeWidth={2} /> : <Save className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 bg-white" style={{ borderBottom: `1px solid ${LINE}` }}>
        <div className="h-full transition-all duration-300" style={{ width: `${progressPct}%`, backgroundColor: GOLD }} />
      </div>

      {/* Product Hero */}
      <div className="relative bg-white">
        <div className="aspect-[4/5] overflow-hidden">
          <img src={productImage || `https://placehold.co/500x625/FAF9F6/D4AF37?text=${encodeURIComponent(productName || categoryName)}`}
            alt={productName || categoryName} className="w-full h-full object-cover"
            onError={e => { e.target.src = `https://placehold.co/500x625/FAF9F6/D4AF37?text=${encodeURIComponent(categoryName)}`; }} />
        </div>
        <span className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 text-[9px] tracking-[0.1em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
          <Flame className="w-3 h-3" strokeWidth={2} /> Bestseller
        </span>
        <button data-testid="like-btn" onClick={() => setLiked(!liked)}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/80" style={{ backdropFilter: 'blur(4px)' }}>
          <Heart className="w-4 h-4" strokeWidth={1.5} style={{ color: liked ? '#991B1B' : MUTED, fill: liked ? '#991B1B' : 'transparent' }} />
        </button>
      </div>

      {/* Product Info */}
      <div className="px-4 py-4 bg-white border-b" style={{ borderColor: LINE }}>
        <p className="text-[10px] tracking-[0.1em] uppercase mb-1" style={{ color: MUTED }}>{categoryName}</p>
        <h1 data-testid="product-title" className="text-[22px] leading-tight mb-2" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
          {productName || categoryName}
        </h1>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(n => <Star key={n} className="w-3 h-3" strokeWidth={1.5} style={{ color: GOLD, fill: GOLD }} />)}
          </div>
          <span className="text-[11px]" style={{ color: MUTED }}>4.8 • 2,341 reviews</span>
        </div>
        <div className="flex items-end gap-2 mb-3">
          <span data-testid="total-price" className="text-[28px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
            ₹{totalPrice.toLocaleString('en-IN')}
          </span>
          <span className="text-[13px] line-through pb-1" style={{ color: MUTED }}>
            ₹{Math.round(totalPrice * 1.4).toLocaleString('en-IN')}
          </span>
          <span className="text-[11px] px-1.5 py-0.5 pb-0.5" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>
            SAVE 28%
          </span>
        </div>
        {customPrice > 0 && (
          <p className="text-[10px] mb-2" style={{ color: MUTED }}>
            Base ₹{parsedBase.toLocaleString('en-IN')} + Customization ₹{customPrice.toLocaleString('en-IN')}{qty > 1 ? ` × ${qty}` : ''}
          </p>
        )}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px]">
          <span className="flex items-center gap-1" style={{ color: '#166534' }}>
            <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> Free delivery by {deliveryStr}
          </span>
          <span className="flex items-center gap-1" style={{ color: MUTED }}>
            <Shield className="w-3.5 h-3.5" strokeWidth={1.5} /> 7-day easy return
          </span>
        </div>
      </div>

      {/* Quantity */}
      <div className="px-4 py-3 bg-white border-b flex items-center justify-between" style={{ borderColor: LINE }}>
        <span className="text-[12px]" style={{ color: INK }}>Quantity</span>
        <div className="flex items-center gap-3">
          <button data-testid="qty-minus" onClick={() => setQty(q => Math.max(1, q - 1))}
            className="w-8 h-8 flex items-center justify-center border" style={{ borderColor: LINE }}>
            <Minus className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
          </button>
          <span data-testid="qty-value" className="text-[14px] font-medium w-6 text-center tabular-nums" style={{ color: INK }}>{qty}</span>
          <button data-testid="qty-plus" onClick={() => setQty(q => Math.min(10, q + 1))}
            className="w-8 h-8 flex items-center justify-center border" style={{ borderColor: LINE }}>
            <Plus className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Section progress indicator */}
      <div className="px-4 py-3 bg-white">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] tracking-[0.14em] uppercase" style={{ color: GOLD }}>Design Your Outfit</p>
          <span className="text-[10px]" style={{ color: MUTED }}>{selectedCount} of {SECTIONS.length} selected</span>
        </div>
      </div>

      {/* ─── SMART COLLAPSIBLE SECTIONS ─── */}
      <div className="mt-0 bg-white">
        {SECTIONS.map(s => renderSection(s.id, s.title))}
      </div>

      {/* Order Summary */}
      <div className="px-4 py-4 mt-2">
        <div data-testid="order-summary" className="bg-white border p-4" style={{ borderColor: LINE }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: MUTED }}>Order Summary</p>
          <div className="space-y-1.5 text-[12px]">
            <div className="flex justify-between"><span style={{ color: MUTED }}>Base Price</span><span style={{ color: INK }}>₹{parsedBase.toLocaleString('en-IN')}</span></div>
            {SECTIONS.map(s => {
              const p = priceFor(s.id);
              if (p <= 0) return null;
              return (
                <div key={s.id} className="flex justify-between">
                  <span style={{ color: MUTED }}>{s.title}: {labelFor(s.id)}</span>
                  <span style={{ color: GOLD }}>+₹{p}</span>
                </div>
              );
            })}
            {qty > 1 && <div className="flex justify-between"><span style={{ color: MUTED }}>Quantity</span><span style={{ color: INK }}>× {qty}</span></div>}
            <div className="pt-2 border-t flex justify-between items-end" style={{ borderColor: '#F0F0F0' }}>
              <span className="font-medium" style={{ color: INK }}>Total</span>
              <span className="text-[20px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>₹{totalPrice.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="px-4 mb-2 flex items-center justify-center gap-4 text-[10px]" style={{ color: MUTED }}>
        <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: '#166534' }} strokeWidth={2} />Free Alterations</span>
        <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: '#166534' }} strokeWidth={2} />Verified Tailors</span>
        <span className="flex items-center gap-1"><Check className="w-3 h-3" style={{ color: '#166534' }} strokeWidth={2} />Quality Promise</span>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <div className="px-4 mb-4">
          <p className="text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: MUTED }}>You may also like</p>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {related.map(p => (
              <button key={p.id} data-testid={`related-${p.id}`}
                onClick={() => navigate('/design-studio', { state: { gender, category, categoryName: CATALOG[gk]?.[category]?.name, basePrice: `₹${p.price}`, productId: p.id, productName: p.name, productImage: p.image } })}
                className="flex-shrink-0 w-28 bg-white border" style={{ borderColor: LINE }}>
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy"
                    onError={e => { e.target.src = `https://placehold.co/280x373/FAF9F6/D4AF37?text=${encodeURIComponent(p.name)}`; }} />
                </div>
                <div className="p-2">
                  <p className="text-[10px] truncate" style={{ color: INK }}>{p.name}</p>
                  <p className="text-[11px] font-medium" style={{ color: INK }}>₹{p.price.toLocaleString('en-IN')}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t px-3 pt-2.5 pb-3"
        style={{ borderColor: LINE, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255,255,255,0.96)' }}>
        {/* Trust badges just above CTA */}
        <div className="flex items-center justify-center gap-3 mb-2 text-[9px]" style={{ color: MUTED }}>
          <span className="flex items-center gap-0.5"><Shield className="w-2.5 h-2.5" strokeWidth={1.5} />Secure</span>
          <span className="flex items-center gap-0.5"><Truck className="w-2.5 h-2.5" strokeWidth={1.5} />Free Delivery</span>
          <span className="flex items-center gap-0.5"><RefreshCcw className="w-2.5 h-2.5" strokeWidth={1.5} />7-Day Return</span>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-to-cart-btn" onClick={handleOrder}
            className="flex-1 py-3 text-[11px] tracking-[0.06em] uppercase flex items-center justify-center gap-1.5 border"
            style={{ borderColor: INK, color: INK }}>
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to Cart
          </button>
          <button data-testid="buy-now-btn" onClick={handleOrder}
            className="flex-[1.3] py-3 text-[11px] tracking-[0.06em] uppercase text-white flex items-center justify-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            Buy Now • ₹{totalPrice.toLocaleString('en-IN')}
          </button>
        </div>
        {isPrivileged && (
          <button data-testid="admin-skip-scan-btn" onClick={handleAdminTestCheckout}
            className="w-full mt-2 py-2.5 text-[10px] tracking-[0.08em] uppercase border border-dashed"
            style={{ borderColor: '#991B1B', color: '#991B1B' }}>
            Skip Body Scan (Admin Testing Only)
          </button>
        )}
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}
        .line-clamp-1{display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}
      `}</style>
    </div>
  );
};

export default DesignStudio;
